import { z } from 'zod';
import { contentIntelligenceGitWorkAssessmentSchema } from './content-intelligence-git-work-dto';
import { contentIntelligenceTicketQualityAssessmentSchema } from './content-intelligence-ticket-quality-dto';

export const MAX_CONTENT_INTELLIGENCE_ASSESSMENT_BATCH_ITEMS = 100;

const assessmentItemIdsSchema = z
  .array(z.number().int().positive())
  .min(1)
  .max(MAX_CONTENT_INTELLIGENCE_ASSESSMENT_BATCH_ITEMS)
  .superRefine((itemIds, context) => {
    if (new Set(itemIds).size !== itemIds.length) {
      context.addIssue({ code: 'custom', message: 'Assessment item ids must be unique' });
    }
  });

export const contentIntelligenceAssessmentBatchRequestSchema = z
  .object({ itemIds: assessmentItemIdsSchema })
  .strict();

export const contentIntelligenceAssessmentBatchResponseSchema = z
  .object({
    loopId: z.number().int().positive(),
    requestedItemIds: assessmentItemIdsSchema,
    ticketAssessments: z
      .array(contentIntelligenceTicketQualityAssessmentSchema)
      .max(MAX_CONTENT_INTELLIGENCE_ASSESSMENT_BATCH_ITEMS),
    gitWorkAssessments: z
      .array(contentIntelligenceGitWorkAssessmentSchema)
      .max(MAX_CONTENT_INTELLIGENCE_ASSESSMENT_BATCH_ITEMS),
    dataSource: z.literal('sir-assessment-batch'),
  })
  .superRefine((response, context) => {
    const requestedItemIds = new Set(response.requestedItemIds);
    const validateAssessment = (
      assessment: { itemId: number; loopId: number },
      path: 'ticketAssessments' | 'gitWorkAssessments',
      index: number,
    ): void => {
      if (assessment.loopId !== response.loopId) {
        context.addIssue({
          code: 'custom',
          message: 'Assessment loop must match the requested loop',
          path: [path, index, 'loopId'],
        });
      }
      if (!requestedItemIds.has(assessment.itemId)) {
        context.addIssue({
          code: 'custom',
          message: 'Assessment item must be included in requested item ids',
          path: [path, index, 'itemId'],
        });
      }
    };

    response.ticketAssessments.forEach((assessment, index) => {
      validateAssessment(assessment, 'ticketAssessments', index);
    });
    response.gitWorkAssessments.forEach((assessment, index) => {
      validateAssessment(assessment, 'gitWorkAssessments', index);
    });
  });

export type ContentIntelligenceAssessmentBatchRequest = z.infer<
  typeof contentIntelligenceAssessmentBatchRequestSchema
>;
export type ContentIntelligenceAssessmentBatchResponse = z.infer<
  typeof contentIntelligenceAssessmentBatchResponseSchema
>;
